import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import { History, ChevronRight, Search } from "lucide-react";
import { api } from "../lib/api";
import { TYPE_META } from "../components/AgendaItem";
import TaskForm from "../components/TaskForm";
import AddCicloButton from "../components/AddCicloButton";
import { formatDate } from "../lib/format";

const SHORTCUTS = [
  { key: "7d", label: "Ultimi 7gg", offset: 7 },
  { key: "30d", label: "Ultimi 30gg", offset: 30 },
  { key: "month", label: "Questo mese", type: "cm" },
  { key: "last-month", label: "Mese scorso", type: "lm" },
  { key: "year", label: "Quest'anno", type: "cy" },
];

const TYPE_OPTS = [
  { v: "task", label: "Ciclo" },
  { v: "deadline", label: "Scadenza" },
  { v: "urgency", label: "Urgenza" },
  { v: "event", label: "Evento" },
  { v: "call", label: "Call" },
  { v: "meeting", label: "Meeting" },
  { v: "appointment", label: "Appuntamento" },
  { v: "payment_reminder", label: "Pagamento" },
  { v: "followup", label: "Follow-up" },
  { v: "habit", label: "Abitudine" },
  { v: "note", label: "Nota" },
];

const GROUP_OPTS = [
  { v: "day", label: "Per giorno" },
  { v: "week", label: "Per settimana" },
  { v: "month", label: "Per mese" },
  { v: "area", label: "Per area" },
  { v: "type", label: "Per tipo" },
  { v: "none", label: "Piatto" },
];

function isoDate(d) { return d.toISOString().slice(0, 10); }

function applyShortcut(sc) {
  const today = new Date();
  if (sc.offset) {
    const from = new Date(); from.setDate(today.getDate() - sc.offset);
    return { from: isoDate(from), to: isoDate(today) };
  }
  if (sc.type === "cm") return { from: isoDate(new Date(today.getFullYear(), today.getMonth(), 1)), to: isoDate(today) };
  if (sc.type === "lm") {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    return { from: isoDate(start), to: isoDate(end) };
  }
  if (sc.type === "cy") return { from: `${today.getFullYear()}-01-01`, to: isoDate(today) };
  return {};
}

export default function Storico() {
  const [params, setParams] = useSearchParams();
  const [areas, setAreas] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  // filters (mirrored to URL)
  const type = params.get("type") || "";
  const areaId = params.get("area_id") || "";
  const groupBy = params.get("group_by") || "day";
  const cf = params.get("completed_from") || "";
  const ct = params.get("completed_to") || "";
  const qtext = params.get("q") || "";

  const setP = (patch) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === "") next.delete(k);
      else next.set(k, v);
    }
    setParams(next);
  };

  useEffect(() => { api.listAreas().then(setAreas).catch(() => {}); }, []);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    const p = { group_by: groupBy };
    if (type) p.type = type;
    if (areaId) p.area_id = areaId;
    if (cf) p.completed_from = cf;
    if (ct) p.completed_to = ct;
    if (qtext) p.q = qtext;
    api.taskHistory(p)
      .then((r) => { if (!ignore) setData(r); })
      .catch(() => toast.error("Errore caricamento storico"))
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [type, areaId, groupBy, cf, ct, qtext]);

  const areaById = useMemo(() => Object.fromEntries(areas.map((a) => [a.id, a])), [areas]);

  return (
    <div className="space-y-5" data-testid="storico-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest opacity-70">Operatività</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold inline-flex items-center gap-2">
            <History size={26}/> Cicli chiusi
          </h1>
          <p className="mt-2 text-sm opacity-70">Cosa hai completato o annullato · rivedibile qui.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/tasks/search" className="rounded-full border px-3 py-1.5 text-sm inline-flex items-center gap-1" data-testid="storico-goto-search" title="Apri ricerca avanzata con più filtri">
            <Search size={14}/> Ricerca avanzata
          </Link>
          <AddCicloButton onSaved={() => setP({ _r: String(Date.now()) })} />
        </div>
      </div>

      {/* Filters row */}
      <div className="pxos-card p-3 space-y-3" data-testid="storico-filters">
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs flex-1 min-w-[140px]">
            <div className="opacity-70 mb-1">Tipo</div>
            <select className="pxos-input" value={type} onChange={(e) => setP({ type: e.target.value })} data-testid="storico-filter-type">
              <option value="">Tutti</option>
              {TYPE_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </label>
          <label className="text-xs flex-1 min-w-[160px]">
            <div className="opacity-70 mb-1">Area</div>
            <select className="pxos-input" value={areaId} onChange={(e) => setP({ area_id: e.target.value })} data-testid="storico-filter-area">
              <option value="">Tutte</option>
              {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </label>
          <label className="text-xs flex-1 min-w-[130px]">
            <div className="opacity-70 mb-1">Completato dal</div>
            <input type="date" className="pxos-input" value={cf} onChange={(e) => setP({ completed_from: e.target.value })} data-testid="storico-completed-from"/>
          </label>
          <label className="text-xs flex-1 min-w-[130px]">
            <div className="opacity-70 mb-1">Completato al</div>
            <input type="date" className="pxos-input" value={ct} onChange={(e) => setP({ completed_to: e.target.value })} data-testid="storico-completed-to"/>
          </label>
          <label className="text-xs flex-1 min-w-[200px]">
            <div className="opacity-70 mb-1">Cerca (titolo/descrizione)</div>
            <input className="pxos-input" placeholder="Es. call, revisione…" value={qtext} onChange={(e) => setP({ q: e.target.value })} data-testid="storico-q"/>
          </label>
          <label className="text-xs flex-1 min-w-[140px]">
            <div className="opacity-70 mb-1">Raggruppa</div>
            <select className="pxos-input" value={groupBy} onChange={(e) => setP({ group_by: e.target.value })} data-testid="storico-groupby">
              {GROUP_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-widest opacity-60 pr-1">Shortcut</span>
          {SHORTCUTS.map((sc) => (
            <button key={sc.key} onClick={() => { const r = applyShortcut(sc); setP({ completed_from: r.from, completed_to: r.to }); }}
                    className="rounded-full border px-2.5 py-1 text-[11px] hover:bg-black/[0.03]"
                    title={`Filtra: ${sc.label}`}
                    data-testid={`storico-shortcut-${sc.key}`}>{sc.label}</button>
          ))}
        </div>
      </div>

      {loading && <div className="pxos-card p-6 text-center opacity-60" data-testid="storico-loading">Caricamento…</div>}

      {!loading && data && (
        <>
          <div className="text-xs opacity-70" data-testid="storico-summary">
            {data.count} risultati · periodo {data.range?.from ? new Date(data.range.from).toLocaleDateString("it-IT") : "—"} → {data.range?.to ? new Date(data.range.to).toLocaleDateString("it-IT") : "—"}
          </div>
          {data.group_by === "none" ? (
            <FlatList items={data.items} areaById={areaById} onEdit={setEditing}/>
          ) : (
            <div className="space-y-4">
              {(data.groups || []).map((g) => (
                <section key={g.key} className="pxos-card p-3" data-testid="storico-group">
                  <h2 className="font-display text-base font-semibold px-2 py-1 flex items-center justify-between">
                    <span data-testid="storico-group-label">{g.label}</span>
                    <span className="text-xs opacity-60" data-testid="storico-group-count">{g.count} risultat{g.count === 1 ? "o" : "i"}</span>
                  </h2>
                  <div className="divide-y" style={{ borderColor: "var(--pxos-border)" }}>
                    {g.items.map((it) => <HistoryRow key={it.id} task={it} area={areaById[it.area_id]} onEdit={setEditing}/>)}
                  </div>
                </section>
              ))}
              {(!data.groups || !data.groups.length) && (
                <div className="pxos-card p-10 text-center opacity-70" data-testid="storico-empty">
                  Nessun ciclo completato nel periodo selezionato.
                </div>
              )}
            </div>
          )}
        </>
      )}

      <TaskForm open={!!editing} initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); setP({}); }}/>
    </div>
  );
}

function HistoryRow({ task, area, onEdit }) {
  const meta = TYPE_META[task.type] || TYPE_META.task;
  const Icon = meta.Icon;
  const completedAt = task.completed_at ? new Date(task.completed_at) : null;
  return (
    <div className="flex items-center gap-3 py-2 px-2 hover:bg-black/[0.02] rounded-lg cursor-pointer"
         onClick={() => onEdit(task)}
         data-testid="storico-row">
      <div className="w-1 h-8 rounded" style={{ background: `var(${meta.varName})` }} aria-hidden />
      <Icon size={14} className="opacity-70" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate" data-testid="storico-row-title">
          <span style={{ opacity: task.status === "cancelled" ? 0.55 : 1, textDecoration: task.status === "cancelled" ? "line-through" : "none" }}>
            {task.title}
          </span>
        </div>
        <div className="text-[11px] opacity-60 flex items-center gap-2 mt-0.5">
          <span className="pxos-badge" style={{ background: `color-mix(in srgb, var(${meta.varName}) 15%, #FFFFFF)`, borderColor: `color-mix(in srgb, var(${meta.varName}) 40%, transparent)` }}>{meta.label}</span>
          {area && (
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full" style={{ background: area.color || "#B8B8B8" }}/>
              {area.name}
            </span>
          )}
          <span>· {task.status === "cancelled" ? "Annullato" : "Completato"} {completedAt ? "il " + completedAt.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>
        </div>
      </div>
      <ChevronRight size={16} className="opacity-40"/>
    </div>
  );
}

function FlatList({ items, areaById, onEdit }) {
  if (!items?.length) return <div className="pxos-card p-10 text-center opacity-70">Nessun risultato.</div>;
  return (
    <div className="pxos-card p-3">
      <div className="divide-y" style={{ borderColor: "var(--pxos-border)" }}>
        {items.map((it) => <HistoryRow key={it.id} task={it} area={areaById[it.area_id]} onEdit={onEdit}/>)}
      </div>
    </div>
  );
}
